import {CurrencyPipe} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {FeePlan, FeeRule, FeeTier, FlowDirection} from '@gatewee/types/fee';
import {PaymentMethod} from '@gatewee/types/payment';
import {TranslateService} from '@ngx-translate/core';
import {Subject, takeUntil} from 'rxjs';

export interface FeePlanDto {
  name: string;
  description: string;
  rules: Omit<FeeRule, 'id'>[];
}

@Component({
  selector: 'gatewee-fee-plan-builder',
  templateUrl: './fee-plan-builder.component.html',
  styleUrls: ['./fee-plan-builder.component.css'],
  standalone: false,
  providers: [CurrencyPipe],
})
export class FeePlanBuilderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() plan?: FeePlan;
  @Output() planSubmit = new EventEmitter<FeePlanDto>();
  @Output() cancel = new EventEmitter<void>();

  readonly FlowDirection = FlowDirection;
  readonly PaymentMethod = PaymentMethod;

  form!: FormGroup;

  readonly operationOptions = [
    {
      value: FlowDirection.INCOME,
      labelKey: 'features.feePlans.form.operationIncome',
    },
    {
      value: FlowDirection.CASH_OUT,
      labelKey: 'features.feePlans.form.operationCashOut',
    },
  ];

  readonly paymentMethodOptions = [
    {value: null, labelKey: 'features.feePlans.form.paymentMethodAny'},
    {
      value: PaymentMethod.PIX,
      labelKey: 'features.feePlans.form.paymentMethodPix',
    },
    {
      value: PaymentMethod.CREDIT_CARD,
      labelKey: 'features.feePlans.form.paymentMethodCreditCard',
    },
    {
      value: PaymentMethod.SLIPIX,
      labelKey: 'features.feePlans.form.paymentMethodSlipix',
    },
    {
      value: PaymentMethod.BANK_SLIP,
      labelKey: 'features.feePlans.form.paymentMethodBankSlip',
    },
  ];

  readonly brandOptions = [
    {value: null, labelKey: 'features.feePlans.form.brandAny'},
    {value: 'VISA', label: 'Visa'},
    {value: 'MASTERCARD', label: 'Mastercard'},
    {value: 'ELO', label: 'Elo'},
    {value: 'AMEX', label: 'Amex'},
    {value: 'HIPERCARD', label: 'Hipercard'},
  ];

  operationSelectItems: {value: FlowDirection; label: string}[] = [];
  paymentMethodSelectItems: {value: PaymentMethod | null; label: string}[] = [];
  installmentSelectItems: {value: number | null; label: string}[] = [];
  brandSelectItems: {value: string | null; label: string}[] = [];

  private readonly destroy$ = new Subject<void>();
  /** Unsubscribes paymentMethod listeners when the rules FormArray is rebuilt. */
  private readonly ruleListenersTeardown$ = new Subject<void>();

  /** Sample amount for live fee preview */
  previewAmount = 500;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private currencyPipe: CurrencyPipe,
  ) {}

  ngOnInit(): void {
    this.rebuildSelectItems();
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.rebuildSelectItems());
    this.buildForm();
  }

  ngOnDestroy(): void {
    this.ruleListenersTeardown$.next();
    this.ruleListenersTeardown$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['plan'] && !changes['plan'].firstChange) {
      this.buildForm();
    }
  }

  private rebuildSelectItems(): void {
    this.operationSelectItems = this.operationOptions.map(op => ({
      value: op.value,
      label: this.translate.instant(op.labelKey),
    }));
    this.paymentMethodSelectItems = this.paymentMethodOptions.map(pm => ({
      value: pm.value,
      label: this.translate.instant(pm.labelKey),
    }));
    this.installmentSelectItems = [
      {
        value: null,
        label: this.translate.instant('features.feePlans.form.installmentsAny'),
      },
      ...[
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
        21, 22, 23, 24,
      ].map(n => ({
        value: n,
        label: `${n}x`,
      })),
    ];
    this.brandSelectItems = this.brandOptions.map(b => ({
      value: b.value,
      label:
        'label' in b && b.label
          ? b.label
          : this.translate.instant((b as {labelKey: string}).labelKey),
    }));
  }

  private buildForm(): void {
    this.ruleListenersTeardown$.next();
    this.form = this.fb.group({
      name: [
        this.plan?.name ?? '',
        [Validators.required, Validators.maxLength(100)],
      ],
      description: [this.plan?.description ?? '', Validators.maxLength(500)],
      rules: this.fb.array(
        this.plan?.rules?.length
          ? this.plan.rules.map(r => this.buildRuleGroup(r))
          : [],
      ),
    });
    this.rulesArray.controls.forEach((_, i) =>
      this.attachPaymentMethodListener(i),
    );
  }

  private buildRuleGroup(rule?: Partial<FeeRule>): FormGroup {
    return this.fb.group({
      operation: [rule?.operation ?? FlowDirection.INCOME, Validators.required],
      paymentMethod: [rule?.paymentMethod ?? null],
      installments: [rule?.installments ?? null],
      brand: [rule?.brand ?? null],
      tiers: this.fb.array(
        rule?.tiers?.length
          ? rule.tiers
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map(t => this.buildTierGroup(t))
          : [this.buildTierGroup()],
        this.tiersValidator,
      ),
    });
  }

  private buildTierGroup(tier?: Partial<FeeTier>): FormGroup {
    return this.fb.group({
      minAmount: [
        this.toNumberOrFallback(tier?.minAmount, 0),
        [Validators.required, Validators.min(0)],
      ],
      maxAmount: [this.toNumberOrNull(tier?.maxAmount)],
      fixedAmount: [
        this.toNumberOrFallback(tier?.fixedAmount, 0),
        [Validators.required, Validators.min(0)],
      ],
      rateValue: [
        this.toNumberOrFallback(tier?.rateValue, 0),
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      sortOrder: [tier?.sortOrder ?? 0, Validators.required],
    });
  }

  private tiersValidator(control: AbstractControl): ValidationErrors | null {
    const array = control as FormArray;
    const tiers = array.controls.map(c => c.value as FeeTier);
    if (!tiers.length) return null;

    const sorted = [...tiers].sort((a, b) => a.minAmount - b.minAmount);
    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];
      if (current.maxAmount === null) {
        return {tierCatchAllNotLast: true};
      }
      if (current.maxAmount !== next.minAmount) {
        return {tierGapOrOverlap: true};
      }
    }
    return null;
  }

  get rulesArray(): FormArray {
    return this.form.get('rules') as FormArray;
  }

  tiersArrayFor(ruleIndex: number): FormArray {
    return this.rulesArray.at(ruleIndex).get('tiers') as FormArray;
  }

  addRule(): void {
    this.rulesArray.push(this.buildRuleGroup());
    this.attachPaymentMethodListener(this.rulesArray.length - 1);
  }

  private attachPaymentMethodListener(ruleIndex: number): void {
    const rule = this.rulesArray.at(ruleIndex);
    rule
      .get('paymentMethod')
      ?.valueChanges.pipe(
        takeUntil(this.ruleListenersTeardown$),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.onPaymentMethodChange(ruleIndex));
  }

  removeRule(index: number): void {
    this.rulesArray.removeAt(index);
  }

  addTier(ruleIndex: number): void {
    const tiersArray = this.tiersArrayFor(ruleIndex);
    const lastTier = tiersArray.length
      ? (tiersArray.at(tiersArray.length - 1).value as FeeTier)
      : null;
    const newMin = lastTier?.maxAmount ?? lastTier?.minAmount ?? 0;
    tiersArray.push(
      this.buildTierGroup({
        minAmount: newMin,
        maxAmount: null,
        fixedAmount: 0,
        rateValue: 0,
        sortOrder: tiersArray.length,
      }),
    );
    this.updateSortOrders(ruleIndex);
  }

  removeTier(ruleIndex: number, tierIndex: number): void {
    const tiersArray = this.tiersArrayFor(ruleIndex);
    tiersArray.removeAt(tierIndex);
    this.updateSortOrders(ruleIndex);
  }

  private updateSortOrders(ruleIndex: number): void {
    const tiersArray = this.tiersArrayFor(ruleIndex);
    tiersArray.controls.forEach((ctrl, i) => {
      ctrl.patchValue({sortOrder: i}, {emitEvent: false});
    });
  }

  isCreditCard(ruleIndex: number): boolean {
    return (
      this.rulesArray.at(ruleIndex).get('paymentMethod')?.value ===
      PaymentMethod.CREDIT_CARD
    );
  }

  onPaymentMethodChange(ruleIndex: number): void {
    const rule = this.rulesArray.at(ruleIndex);
    if (!this.isCreditCard(ruleIndex)) {
      rule.patchValue({installments: null, brand: null});
    }
  }

  computeFeePreview(ruleIndex: number, tierIndex: number): string {
    const tier = this.tiersArrayFor(ruleIndex).at(tierIndex).value as FeeTier;
    const amount = this.previewAmount;
    const total = +tier.fixedAmount + (+amount * +tier.rateValue) / 100;
    return this.currencyPipe.transform(total, 'BRL') ?? '';
  }

  hasTierError(ruleIndex: number): boolean {
    const tiersArray = this.tiersArrayFor(ruleIndex);
    return tiersArray.invalid && tiersArray.touched;
  }

  getTierErrorKey(ruleIndex: number): string {
    const errors = this.tiersArrayFor(ruleIndex).errors;
    if (errors?.['tierCatchAllNotLast'])
      return 'features.feePlans.form.errorCatchAllNotLast';
    if (errors?.['tierGapOrOverlap'])
      return 'features.feePlans.form.errorTierGapOrOverlap';
    return '';
  }

  private extractSelectValue<T>(raw: unknown): T | undefined {
    if (raw === null || raw === undefined) {
      return undefined;
    }
    if (typeof raw === 'object' && raw !== null && 'value' in raw) {
      return (raw as {value: T | undefined}).value ?? undefined;
    }
    return raw as T | undefined;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const raw = value.trim().replace(/\s/g, '');
      if (!raw) return null;

      // API-friendly format (e.g. 17.9100) -> parse directly as decimal.
      if (/^-?\d+(\.\d+)?$/.test(raw)) {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
      }

      // Locale decimal format using comma (e.g. 17,91).
      if (/^-?\d+(,\d+)?$/.test(raw)) {
        const parsed = Number(raw.replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : null;
      }

      // Mixed separators (e.g. 1.234,56 or 1,234.56): infer decimal by last separator.
      const lastComma = raw.lastIndexOf(',');
      const lastDot = raw.lastIndexOf('.');
      const decimalSeparator =
        lastComma > lastDot ? ',' : lastDot > lastComma ? '.' : null;
      const normalized = decimalSeparator
        ? raw
            .replace(/[.,]/g, match => (match === decimalSeparator ? '@' : ''))
            .replace('@', '.')
        : raw.replace(/[.,]/g, '');

      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private toNumberOrFallback(value: unknown, fallback: number): number {
    const parsed = this.toNumberOrNull(value);
    return parsed ?? fallback;
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const value = this.form.value;
    const dto: FeePlanDto = {
      name: value.name,
      description: value.description ?? '',
      rules: value.rules.map(
        (r: FeeRule & {tiers: FeeTier[]}, ruleIndex: number) => ({
          operation:
            this.extractSelectValue<FlowDirection>(r.operation) ??
            FlowDirection.INCOME,
          paymentMethod: this.extractSelectValue<PaymentMethod>(
            r.paymentMethod,
          ),
          installments: this.extractSelectValue<number>(r.installments),
          brand: this.extractSelectValue<string>(r.brand),
          tiers: this.tiersArrayFor(ruleIndex).controls.map((tc, i) => ({
            ...tc.value,
            minAmount: this.toNumberOrFallback(tc.value.minAmount, 0),
            maxAmount: this.toNumberOrNull(tc.value.maxAmount),
            fixedAmount: this.toNumberOrFallback(tc.value.fixedAmount, 0),
            rateValue: this.toNumberOrFallback(tc.value.rateValue, 0),
            sortOrder: i,
          })),
        }),
      ),
    };
    this.planSubmit.emit(dto);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onPreviewAmountChange(
    val: string | number | object | null | undefined,
  ): void {
    const n = this.toNumberOrNull(val);
    this.previewAmount = n !== null && n >= 0 ? n : 0;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
